import { useCallback } from 'react'
import { usePage } from '@inertiajs/react'
import {
  formatMissedDateDisplay,
  formatSiteDateTime,
  formatTimelineTooltip,
  formatWallClockDateTimeDisplay,
} from '@/shared/lib/tenantDateTime'
import type { SharedProps } from '@/shared/types/SharedProps'

/**
 * Canonical accessor for "time + site zone" display (REBUILD-611 / REBUILD-613). Every
 * formatter appends the site zone abbreviation right after the time. The zone is the record's
 * own `siteTimeZone` when the caller has one, else the shared `tenantTimeZone` (site → tenant →
 * default fallback computed by the backend) — never the browser zone.
 */
export const useSiteDateTime = () => {
  const { tenantTimeFormat, tenantTimeZone } = usePage<SharedProps>().props

  const formatDateTime = useCallback(
    (localString: string, siteTimeZone?: string | null) =>
      formatSiteDateTime(localString, siteTimeZone ?? tenantTimeZone, tenantTimeFormat),
    [tenantTimeFormat, tenantTimeZone],
  )

  const formatWallClockDisplay = useCallback(
    (wallClockString: string | null | undefined, siteTimeZone?: string | null) =>
      formatWallClockDateTimeDisplay(
        wallClockString,
        tenantTimeFormat,
        siteTimeZone ?? tenantTimeZone,
      ),
    [tenantTimeFormat, tenantTimeZone],
  )

  const formatMissedDisplay = useCallback(
    (
      dateOnlyString: string,
      missedTimeLocal: string | null | undefined,
      siteTimeZone?: string | null,
    ) =>
      formatMissedDateDisplay(
        dateOnlyString,
        missedTimeLocal,
        tenantTimeFormat,
        siteTimeZone ?? tenantTimeZone,
      ),
    [tenantTimeFormat, tenantTimeZone],
  )

  const formatTooltip = useCallback(
    (dateStr: string, siteTimeZone?: string | null) =>
      formatTimelineTooltip(dateStr, tenantTimeFormat, siteTimeZone ?? tenantTimeZone),
    [tenantTimeFormat, tenantTimeZone],
  )

  return { formatDateTime, formatWallClockDisplay, formatMissedDisplay, formatTooltip }
}
